import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Check, Monitor, Tablet, Smartphone, RefreshCw, ExternalLink, ArrowLeft, Trash2,
  Plus, ChevronUp, ChevronDown, Lock, Pencil, AlertTriangle, X, Layers,
} from 'lucide-react';
import {
  BLOCK_MANIFEST,
  BLOCK_BY_ID,
  MAX_BLOCK_DEPTH,
  validateComposition,
  defaultBlocksForArchetype,
  type BlockInstance,
  type BlockManifestEntry,
  type FieldDescriptor,
  type CompositionIssue,
} from '@h2m/brief/blocks';

/* ── recursive immutable tree utils (operate at any nesting depth) ── */
function mapNode(
  list: BlockInstance[],
  id: string,
  fn: (b: BlockInstance) => BlockInstance,
): BlockInstance[] {
  return list.map((b) =>
    b.id === id ? fn(b) : b.children ? { ...b, children: mapNode(b.children, id, fn) } : b,
  );
}
function removeNode(list: BlockInstance[], id: string): BlockInstance[] {
  return list
    .filter((b) => b.id !== id)
    .map((b) => (b.children ? { ...b, children: removeNode(b.children, id) } : b));
}
function moveNode(list: BlockInstance[], id: string, dir: -1 | 1): BlockInstance[] {
  const i = list.findIndex((b) => b.id === id);
  if (i >= 0) {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    const [item] = next.splice(i, 1);
    if (!item) return list;
    next.splice(j, 0, item);
    return next;
  }
  return list.map((b) => (b.children ? { ...b, children: moveNode(b.children, id, dir) } : b));
}
function addNode(list: BlockInstance[], parentId: string | undefined, fresh: BlockInstance): BlockInstance[] {
  if (parentId) {
    return mapNode(list, parentId, (b) => ({ ...b, children: [...(b.children ?? []), fresh] }));
  }
  const footerIdx = list.findIndex((b) => BLOCK_BY_ID[b.type]?.position === 'bottom');
  if (footerIdx < 0) return [...list, fresh];
  const next = [...list];
  next.splice(footerIdx, 0, fresh);
  return next;
}

import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SaveBar } from '@/components/ui/save-bar';
import { toast } from '@/components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBrief, useUpdateBriefSlice, useSaveAndRebuildBrief } from '@/lib/brief';
import { useSession } from '@/lib/auth';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';
import { MediaPicker } from '@/components/media/picker';
import { useMediaHost, type MediaAsset } from '@/lib/media';

/** Where a link field can point — internal pages + section anchors. Custom
 * URLs are always allowed via the "własny link" escape hatch. */
interface LinkTarget {
  value: string;
  label: string;
  group: string;
}
const SECTION_ANCHORS: LinkTarget[] = [
  { value: '#uslugi', label: 'Sekcja: Usługi', group: 'Sekcje na stronie' },
  { value: '#zespol', label: 'Sekcja: Zespół', group: 'Sekcje na stronie' },
  { value: '#opinie', label: 'Sekcja: Opinie', group: 'Sekcje na stronie' },
  { value: '#kontakt', label: 'Sekcja: Kontakt', group: 'Sekcje na stronie' },
  { value: '#umow', label: 'Sekcja: Rezerwacja', group: 'Sekcje na stronie' },
];

/**
 * `/strony/<pageId>` — per-page editor.
 *
 * Two flavours of pageId:
 *   - `builtin-home` / `builtin-about` / `builtin-contact` / `builtin-reviews`
 *     → reads global brief.design overlay and shows the standard wyglad form
 *     (skin / archetype / tone / density / disabled-sections, where applicable)
 *   - any other id → custom page from brief.custom_pages[]. Editor shows
 *     title / path / description fields + a per-page design overlay
 *     (skin / tone / density), with a live iframe preview that talks to
 *     `/api/admin/wyglad/preview?page_archetype=...&...`.
 */

export const Route = createFileRoute('/_app/strony/$pageId')({
  staticData: { crumb: 'Edycja strony' },
  component: PageEditor,
});

type SkinId =
  | 'trust-clinical-a' | 'trust-clinical-b' | 'trust-clinical-c'
  | 'boutique-derm' | 'editorial-clinical'
  | 'vet-warm' | 'pediatric-family'
  | 'dental-modern' | 'aesthetic-luxury'
  | 'physio-active' | 'gp-friendly' | 'emergency-bold';
type Tone = 'default' | 'surface' | 'dark' | 'accent';
type Density = 'compact' | 'comfortable' | 'spacious';
type Device = 'desktop' | 'tablet' | 'mobile';
type PageArchetype =
  | 'home' | 'home-boutique' | 'home-editorial'
  | 'about' | 'contact' | 'reviews' | 'procedure'
  | 'article' | 'landing' | 'emergency' | 'booking';

interface CustomPage {
  id: string;
  path: string;
  archetype: PageArchetype;
  title: string;
  description?: string;
  design?: {
    skin?: SkinId;
    tone?: Tone;
    density?: Density;
  };
  created_at?: number;
  updated_at?: number;
}

const BUILTIN: Record<string, { path: string; title: string; archetype: PageArchetype }> = {
  'builtin-home': { path: '/', title: 'Strona główna', archetype: 'home' },
  'builtin-about': { path: '/o-nas', title: 'O nas', archetype: 'about' },
  'builtin-contact': { path: '/kontakt', title: 'Kontakt', archetype: 'contact' },
  'builtin-reviews': { path: '/opinie', title: 'Opinie', archetype: 'reviews' },
};

const SKINS: { id: SkinId; name: string; bg: string; surface: string; text: string; accent: string }[] = [
  { id: 'trust-clinical-a', name: 'Trust Clinical A', bg: '#ffffff', surface: '#f4f6fb', text: '#0d1320', accent: '#1f5eea' },
  { id: 'trust-clinical-b', name: 'Trust Clinical B', bg: '#fbfbf8', surface: '#eef3f1', text: '#1a221d', accent: '#185c5c' },
  { id: 'trust-clinical-c', name: 'Trust Clinical C', bg: '#ffffff', surface: '#f4f5fb', text: '#0c0e1c', accent: '#5246d8' },
  { id: 'boutique-derm', name: 'Boutique Derm', bg: '#faf6f0', surface: '#f1eadc', text: '#231a13', accent: '#97631a' },
  { id: 'editorial-clinical', name: 'Editorial Clinical', bg: '#ffffff', surface: '#f4f4f4', text: '#0a0a0a', accent: '#d6481b' },
  { id: 'vet-warm', name: 'Vet Warm', bg: '#fbf9f4', surface: '#f0eee5', text: '#1c2218', accent: '#46743c' },
  { id: 'pediatric-family', name: 'Pediatric Family', bg: '#fefaf6', surface: '#f7f1ed', text: '#1f1a22', accent: '#d7546a' },
  { id: 'dental-modern', name: 'Dental Modern', bg: '#fbfcfd', surface: '#f1f5f9', text: '#0e1a26', accent: '#00a39c' },
  { id: 'aesthetic-luxury', name: 'Aesthetic Luxury', bg: '#0c0c0a', surface: '#1a1814', text: '#f6f1e6', accent: '#c9a165' },
  { id: 'physio-active', name: 'Physio Active', bg: '#ffffff', surface: '#f3f5f7', text: '#0e1517', accent: '#ff5a36' },
  { id: 'gp-friendly', name: 'GP Friendly', bg: '#fdfaf3', surface: '#f4ede0', text: '#221d12', accent: '#d76b27' },
  { id: 'emergency-bold', name: 'Emergency Bold', bg: '#0a0c10', surface: '#1c2027', text: '#f7fafc', accent: '#ff3b3b' },
];

const TONES: { id: Tone; label: string }[] = [
  { id: 'default', label: 'Domyślny' },
  { id: 'surface', label: 'Stonowany' },
  { id: 'dark', label: 'Ciemny' },
  { id: 'accent', label: 'Akcent' },
];

const DENSITIES: { id: Density; label: string }[] = [
  { id: 'compact', label: 'Gęsto' },
  { id: 'comfortable', label: 'Wygodnie' },
  { id: 'spacious', label: 'Przestrzennie' },
];

const DEVICE_WIDTHS: Record<Device, number> = { desktop: 1280, tablet: 768, mobile: 390 };

function PageEditor() {
  const { pageId } = Route.useParams();
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const saveAndRebuild = useSaveAndRebuildBrief();
  const { data: session } = useSession();
  const tenantSlug = session?.tenant?.slug;
  const navigate = useNavigate();

  const customPages = React.useMemo(() => {
    const c = (brief as unknown as { custom_pages?: CustomPage[] })?.custom_pages ?? [];
    return Array.isArray(c) ? c : [];
  }, [brief]);

  const builtin = BUILTIN[pageId];
  const custom = customPages.find((p) => p.id === pageId);
  const isBuiltin = !!builtin;

  // For built-in pages we read from brief.design (global). For custom pages
  // we read from page.design + fall back to global.
  const globalDesign = React.useMemo(() => {
    const d = (brief as unknown as { design?: { skin?: SkinId; tone?: Tone; density?: Density } })?.design ?? {};
    return d;
  }, [brief]);

  const [title, setTitle] = React.useState(custom?.title ?? builtin?.title ?? '');
  const [path, setPath] = React.useState(custom?.path ?? builtin?.path ?? '');
  const [description, setDescription] = React.useState(custom?.description ?? '');
  const [skin, setSkin] = React.useState<SkinId | undefined>(
    custom?.design?.skin ?? globalDesign.skin
  );
  const [tone, setTone] = React.useState<Tone>(
    custom?.design?.tone ?? globalDesign.tone ?? 'default'
  );
  const [density, setDensity] = React.useState<Density>(
    custom?.design?.density ?? globalDesign.density ?? 'comfortable'
  );
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  // DUPLO block tree. Built-in pages keep the legacy archetype preview;
  // custom pages compose from an explicit, editable tree. Seed from the
  // saved tree, or from the archetype preset the first time (so the page is
  // never blank and matches today's template until the owner edits).
  const [blocks, setBlocks] = React.useState<BlockInstance[]>([]);
  React.useEffect(() => {
    if (dirty) return;
    if (isBuiltin) {
      setBlocks([]);
      return;
    }
    const saved = (custom as { blocks?: BlockInstance[] } | undefined)?.blocks;
    setBlocks(
      Array.isArray(saved) && saved.length > 0
        ? saved
        : defaultBlocksForArchetype((custom?.archetype ?? 'home') as never),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custom, isBuiltin]);

  const compositionIssues: CompositionIssue[] = React.useMemo(
    () => (isBuiltin ? [] : validateComposition(blocks, (custom?.archetype ?? 'home') as never)),
    [blocks, isBuiltin, custom],
  );
  const hasBlockErrors = compositionIssues.some((i) => i.level === 'error');

  // Re-sync when underlying brief reloads.
  React.useEffect(() => {
    if (dirty) return;
    setTitle(custom?.title ?? builtin?.title ?? '');
    setPath(custom?.path ?? builtin?.path ?? '');
    setDescription(custom?.description ?? '');
    setSkin(custom?.design?.skin ?? globalDesign.skin);
    setTone(custom?.design?.tone ?? globalDesign.tone ?? 'default');
    setDensity(custom?.design?.density ?? globalDesign.density ?? 'comfortable');
  }, [custom, builtin, globalDesign, dirty]);

  const markDirty = () => setDirty(true);

  /* ── block tree mutation helpers (immutable, recursive to MAX_BLOCK_DEPTH) ── */
  const newBlockId = (type: string) =>
    `${type.replace(/[^a-z0-9]+/gi, '-')}-${(crypto.randomUUID?.() ?? String(performance.now())).slice(0, 8)}`;

  const mutateBlocks = (fn: (list: BlockInstance[]) => BlockInstance[]) => {
    setBlocks((prev) => fn(prev));
    markDirty();
  };

  // Apply `patch` to the block with `id` anywhere in the tree (any depth).
  const patchBlock = (id: string, patch: (b: BlockInstance) => BlockInstance) =>
    mutateBlocks((list) => mapNode(list, id, patch));

  const setBlockField = (id: string, key: string, value: unknown) =>
    patchBlock(id, (b) => ({ ...b, content: { ...(b.content ?? {}), [key]: value } }));

  const setBlockCondition = (id: string, condition: BlockInstance['condition']) =>
    patchBlock(id, (b) => {
      const next = { ...b };
      if (condition) next.condition = condition;
      else delete next.condition;
      return next;
    });

  const setBlockTone = (id: string, tone: string | undefined) =>
    patchBlock(id, (b) => {
      const layout = { ...(b.layout ?? {}) };
      if (tone) layout.tone = tone as never;
      else delete layout.tone;
      return { ...b, layout: Object.keys(layout).length ? layout : undefined };
    });

  const removeBlock = (id: string) => mutateBlocks((list) => removeNode(list, id));

  // Move a block up/down within ITS OWN sibling array, at any nesting depth.
  const moveBlock = (id: string, dir: -1 | 1) => mutateBlocks((list) => moveNode(list, id, dir));

  const makeBlock = (entry: BlockManifestEntry): BlockInstance => ({
    id: newBlockId(entry.id),
    type: entry.id,
    content: { ...entry.defaultContent },
    source: 'brief',
    ...(entry.acceptsChildren ? { children: [] } : {}),
  });

  // Insert a new block. Top-level inserts land just before the footer (so
  // structure stays bottom-anchored); child inserts append into a container
  // at any depth.
  const addBlock = (entry: BlockManifestEntry, parentId?: string) =>
    mutateBlocks((list) => addNode(list, parentId, makeBlock(entry)));

  // Swap a block to a sibling variant in the same category, preserving any
  // overlapping content keys (so a headline survives hero.media-split →
  // hero.centered).
  const switchVariant = (id: string, newType: string) =>
    patchBlock(id, (b) => {
      const entry = BLOCK_BY_ID[newType];
      return {
        ...b,
        type: newType,
        content: { ...(entry?.defaultContent ?? {}), ...(b.content ?? {}) },
      };
    });

  const archetype: PageArchetype = custom?.archetype ?? builtin?.archetype ?? 'home';

  // Link picker destinations: built-in pages + this clinic's custom pages +
  // section anchors. Custom URLs stay available via the field's escape hatch.
  const linkTargets: LinkTarget[] = React.useMemo(() => {
    const builtinPages: LinkTarget[] = Object.values(BUILTIN).map((b) => ({
      value: b.path,
      label: `${b.title} (${b.path})`,
      group: 'Strony',
    }));
    const customLinks: LinkTarget[] = customPages.map((p) => ({
      value: p.path,
      label: `${p.title} (${p.path})`,
      group: 'Strony',
    }));
    const phone = (brief as unknown as { company?: { phone?: string } })?.company?.phone;
    const tel: LinkTarget[] = phone
      ? [{ value: `tel:${phone.replace(/\s+/g, '')}`, label: `Telefon (${phone})`, group: 'Kontakt' }]
      : [];
    return [...builtinPages, ...customLinks, ...SECTION_ANCHORS, ...tel];
  }, [customPages, brief]);
  const apiBase = env.apiBaseUrl || '';
  const tenantParam = session?.role === 'operator' && tenantSlug ? `&tenant=${tenantSlug}` : '';
  const liveQuery = React.useMemo(() => {
    const parts: string[] = [`page_archetype=${archetype}`];
    if (skin) parts.push(`skin=${skin}`);
    if (tone) parts.push(`tone=${tone}`);
    if (density) parts.push(`density=${density}`);
    if (title) parts.push(`page_title=${encodeURIComponent(title)}`);
    if (description) parts.push(`page_description=${encodeURIComponent(description)}`);
    return parts.join('&');
  }, [archetype, skin, tone, density, title, description]);

  const [debouncedQuery, setDebouncedQuery] = React.useState(liveQuery);
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(liveQuery), 250);
    return () => window.clearTimeout(t);
  }, [liveQuery]);
  const previewUrl = `${apiBase}/api/admin/wyglad/preview?${debouncedQuery}${tenantParam}`;

  const [device, setDevice] = React.useState<Device>('desktop');
  const [reloadKey, setReloadKey] = React.useState(0);

  // Block-tree live preview (custom pages): the iframe can't POST a body, so
  // we fetch the composed HTML and feed it via srcDoc. Same renderer as the
  // cloud build → the preview is truthful. Debounced on every edit.
  const useBlockPreview = !isBuiltin && blocks.length > 0;
  const [srcDoc, setSrcDoc] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!useBlockPreview) {
      setSrcDoc(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void fetch(`${apiBase}/api/admin/wyglad/preview${tenantParam ? `?${tenantParam.slice(1)}` : ''}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ archetype, blocks, skin, tone, density, title, description }),
      })
        .then((r) => r.text())
        .then((html) => {
          if (!cancelled) setSrcDoc(html);
        })
        .catch(() => {
          if (!cancelled) setSrcDoc('<!doctype html><body style="font-family:system-ui;padding:2rem;color:#999">Nie udało się załadować podglądu.</body>');
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
    // reloadKey forces a manual refresh; stringified blocks track edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBlockPreview, archetype, JSON.stringify(blocks), skin, tone, density, title, description, reloadKey]);

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      if (isBuiltin) {
        // Built-in pages just persist any global design tweaks the user
        // made (since they share global brief.design). Keep things simple:
        // route them through the existing design slice.
        await update.mutateAsync({
          slice: 'design',
          data: { ...globalDesign, skin, tone, density },
        });
      } else if (custom) {
        const updated: CustomPage & { blocks?: BlockInstance[] } = {
          ...custom,
          title: title.trim() || custom.title,
          path: path.trim() || custom.path,
          description: description.trim() || undefined,
          design: { skin, tone, density },
          blocks,
          updated_at: Math.floor(Date.now() / 1000),
        };
        const next = customPages.map((p) => (p.id === custom.id ? updated : p));
        await update.mutateAsync({ slice: 'custom_pages', data: next });
      }
      setSavedStatus('saved');
      setDirty(false);
      toast.success('Zapisano');
    } catch {
      setSavedStatus('error');
      toast.error('Nie udało się zapisać');
    }
  };

  const onSaveAndRebuild = async () => {
    setSavedStatus('idle');
    try {
      const patch: Record<string, unknown> = {};
      if (isBuiltin) {
        patch.design = { ...globalDesign, skin, tone, density };
      } else if (custom) {
        const updated: CustomPage & { blocks?: BlockInstance[] } = {
          ...custom,
          title: title.trim() || custom.title,
          path: path.trim() || custom.path,
          description: description.trim() || undefined,
          design: { skin, tone, density },
          blocks,
          updated_at: Math.floor(Date.now() / 1000),
        };
        patch.custom_pages = customPages.map((p) => (p.id === custom.id ? updated : p));
      }
      await saveAndRebuild.mutateAsync({ patch });
      setSavedStatus('saved');
      setDirty(false);
      toast.success('Zapisano i zlecono nowy podgląd');
    } catch {
      setSavedStatus('error');
      toast.error('Nie udało się uruchomić podglądu');
    }
  };

  const onDelete = async () => {
    if (!custom) return;
    try {
      await update.mutateAsync({
        slice: 'custom_pages',
        data: customPages.filter((p) => p.id !== custom.id),
      });
      toast.success('Strona usunięta');
      navigate({ to: '/strony' as never });
    } catch {
      toast.error('Nie udało się usunąć');
    }
  };

  if (!builtin && !custom) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="font-display text-xl font-semibold text-text">Nie znaleziono strony</h1>
        <p className="mt-2 text-sm text-text-muted">
          Strona <code className="font-mono">{pageId}</code> nie istnieje.
        </p>
        <Button asChild className="mt-4">
          <Link to={'/strony' as never}>
            <ArrowLeft className="size-4" aria-hidden="true" /> Wróć do listy
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSave();
      }}
      className="-m-6 md:-m-8 flex h-[calc(100vh-4rem)] flex-col"
    >
      {/* TOP STRIP */}
      <header className="flex items-center justify-between gap-4 border-b border-border bg-bg px-6 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button asChild type="button" variant="ghost" size="icon">
            <Link to={'/strony' as never}>
              <ArrowLeft className="size-4" aria-hidden="true" />
            </Link>
          </Button>
          <div className="min-w-0">
            <h1 className="truncate font-display text-base font-semibold text-text">{title || builtin?.title || 'Strona'}</h1>
            <p className="text-xs text-text-muted">
              <code className="rounded bg-surface px-1 font-mono text-[10px]">{path || builtin?.path}</code>
              {' · '}szablon <strong>{archetype}</strong>
              {isBuiltin ? <span className="ml-2 rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-medium text-text-subtle ring-1 ring-border">wbudowana</span> : null}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <DeviceToggle device={device} onChange={setDevice} />
          <Button type="button" variant="ghost" size="icon" onClick={() => setReloadKey((k) => k + 1)} title="Odśwież podgląd">
            <RefreshCw className="size-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={() => window.open(previewUrl, '_blank', 'noopener')} title="Otwórz w nowej karcie">
            <ExternalLink className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* LEFT: controls */}
        <div className="w-full max-w-md shrink-0 overflow-y-auto border-r border-border bg-bg p-6 scrollbar-thin">
          {!isBuiltin ? (
            <SectionWrap
              title="Bloki — układ strony"
              subtitle="Składaj stronę z gotowych, bezpiecznych bloków. Dodawaj, usuwaj, zmieniaj kolejność i treść — każdy układ pozostaje zgodny z systemem projektowym."
            >
              <BlockComposer
                blocks={blocks}
                archetype={archetype}
                issues={compositionIssues}
                onAdd={addBlock}
                onRemove={removeBlock}
                onMove={moveBlock}
                onSetField={setBlockField}
                onSwitchVariant={switchVariant}
                onSetCondition={setBlockCondition}
                onSetTone={setBlockTone}
                linkTargets={linkTargets}
              />
            </SectionWrap>
          ) : null}

          {!isBuiltin ? (
            <SectionWrap title="Metadane">
              <div className="space-y-3">
                <Field label="Tytuł" htmlFor="t">
                  <Input id="t" value={title} onChange={(e) => { setTitle(e.target.value); markDirty(); }} maxLength={80} />
                </Field>
                <Field label="Ścieżka URL" htmlFor="p">
                  <Input id="p" value={path} onChange={(e) => { setPath(e.target.value); markDirty(); }} className="font-mono" maxLength={80} />
                </Field>
                <Field label="Opis (meta description)" htmlFor="d" hint="80–160 znaków zalecane">
                  <Textarea id="d" value={description} onChange={(e) => { setDescription(e.target.value); markDirty(); }} rows={3} maxLength={200} />
                </Field>
              </div>
            </SectionWrap>
          ) : null}

          <SectionWrap title={`${isBuiltin ? '1' : '2'} · Skórka`} subtitle="Paleta kolorów i typografia. Nadpisuje globalną skórkę dla tej strony.">
            <ul role="list" className="grid grid-cols-2 gap-2">
              {SKINS.map((s) => {
                const isActive = skin === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => { setSkin(s.id); markDirty(); }}
                      className={cn(
                        'group relative block w-full overflow-hidden rounded-md text-left ring-1 transition-all',
                        isActive ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg' : 'ring-black/5 hover:ring-accent/40'
                      )}
                      title={s.name}
                    >
                      <div className="relative h-16" style={{ background: s.bg }}>
                        <div className="absolute inset-x-0 bottom-0 h-6" style={{ background: s.surface }} />
                        <span className="absolute left-2 top-2 inline-block size-4 rounded-full ring-1 ring-white/40" style={{ background: s.accent }} aria-hidden="true" />
                        <div className="absolute bottom-1 left-2 font-display text-xs" style={{ color: s.text }}>Aa</div>
                        {isActive ? (
                          <span className="absolute right-1 top-1 inline-flex size-4 items-center justify-center rounded-full bg-accent text-text-inverted">
                            <Check className="size-2.5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate p-1.5 text-[10px] font-medium text-text">{s.name}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </SectionWrap>

          <SectionWrap title={`${isBuiltin ? '2' : '3'} · Atmosfera`}>
            <div className="grid grid-cols-2 gap-1.5">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setTone(t.id); markDirty(); }}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-xs font-medium ring-1 transition-all',
                    tone === t.id ? 'bg-accent/10 text-accent ring-accent/30' : 'bg-surface text-text-muted ring-border hover:ring-accent/40'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </SectionWrap>

          <SectionWrap title={`${isBuiltin ? '3' : '4'} · Gęstość`}>
            <div className="grid grid-cols-3 gap-1.5">
              {DENSITIES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => { setDensity(d.id); markDirty(); }}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-xs font-medium ring-1 transition-all',
                    density === d.id ? 'bg-accent/10 text-accent ring-accent/30' : 'bg-surface text-text-muted ring-border hover:ring-accent/40'
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </SectionWrap>

          {!isBuiltin ? (
            <div className="mb-6 rounded-md bg-accent/5 p-3 ring-1 ring-accent/20">
              <p className="text-xs text-text-muted">
                <strong>Tip:</strong> kliknij <strong>Zapisz + zbuduj podgląd</strong>, aby nowa strona
                trafiła na publiczną domenę. Cloud build emituje każdą stronę własną razem z
                pozostałymi stronami.
              </p>
            </div>
          ) : null}

          {!isBuiltin ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteOpen(true)}
              className="mb-4 w-full text-error hover:bg-error/10"
            >
              <Trash2 className="size-4" aria-hidden="true" /> Usuń tę stronę
            </Button>
          ) : null}

          {hasBlockErrors ? (
            <div className="mb-3 flex items-start gap-2 rounded-md bg-error/10 p-2.5 text-xs text-error ring-1 ring-error/20">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>Popraw błędy w układzie bloków, zanim zapiszesz — strona nie zbuduje się poprawnie.</span>
            </div>
          ) : null}

          <SaveBar
            saving={update.isPending}
            rebuilding={saveAndRebuild.isPending}
            dirty={dirty && !hasBlockErrors}
            status={savedStatus}
            onCancel={() => {
              setTitle(custom?.title ?? builtin?.title ?? '');
              setPath(custom?.path ?? builtin?.path ?? '');
              setDescription(custom?.description ?? '');
              setSkin(custom?.design?.skin ?? globalDesign.skin);
              setTone(custom?.design?.tone ?? globalDesign.tone ?? 'default');
              setDensity(custom?.design?.density ?? globalDesign.density ?? 'comfortable');
              setDirty(false);
            }}
            onSaveAndRebuild={onSaveAndRebuild}
          />
        </div>

        {/* RIGHT: live preview */}
        <div className="relative flex-1 bg-surface p-4">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all">
            <div
              className="overflow-hidden rounded-md bg-bg shadow-xl ring-1 ring-black/10"
              style={{
                width: `${DEVICE_WIDTHS[device]}px`,
                maxWidth: 'calc(100vw - 32rem)',
                height: 'calc(100vh - 10rem)',
              }}
            >
              <iframe
                key={useBlockPreview ? `srcdoc-${reloadKey}` : `${previewUrl}-${reloadKey}`}
                {...(useBlockPreview ? { srcDoc: srcDoc ?? '' } : { src: previewUrl })}
                title="Podgląd strony"
                className="size-full border-0"
                sandbox="allow-same-origin allow-scripts"
              />
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć stronę „{title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Ścieżka <code className="rounded bg-surface px-1 font-mono text-xs">{path}</code> przestanie
              być serwowana po następnej publikacji.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-error text-text-inverted hover:bg-error/90">
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

function SectionWrap({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="mb-5">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{title}</h2>
      {subtitle ? <p className="mb-2 mt-0.5 text-[11px] text-text-muted">{subtitle}</p> : <div className="h-2" />}
      {children}
    </section>
  );
}

/* ──────────────────────────── DUPLO composer ────────────────────────── */

const CATEGORY_LABELS: Record<string, string> = {
  structure: 'Struktura',
  hero: 'Nagłówek',
  trust: 'Zaufanie',
  services: 'Usługi',
  team: 'Zespół',
  reviews: 'Opinie',
  about: 'O nas',
  contact: 'Kontakt',
  cta: 'Wezwanie (CTA)',
  booking: 'Rezerwacja',
  editorial: 'Treść',
  conversion: 'Konwersja',
  layout: 'Układ',
};

function archetypeAllowsEntry(entry: BlockManifestEntry, archetype: string): boolean {
  return entry.archetypes === 'all' || entry.archetypes.includes(archetype as never);
}

interface ComposerProps {
  blocks: BlockInstance[];
  archetype: string;
  issues: CompositionIssue[];
  onAdd: (entry: BlockManifestEntry, parentId?: string) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onSetField: (id: string, key: string, value: unknown) => void;
  onSwitchVariant: (id: string, newType: string) => void;
  onSetCondition: (id: string, condition: BlockInstance['condition']) => void;
  onSetTone: (id: string, tone: string | undefined) => void;
  linkTargets: LinkTarget[];
}

/** Visibility presets surfaced in the editor → typed Condition values. */
const VISIBILITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'always', label: 'Zawsze' },
  { value: 'mobile', label: 'Tylko telefon' },
  { value: 'desktop', label: 'Tylko desktop' },
  { value: 'until', label: 'Do dnia…' },
  { value: 'from', label: 'Od dnia…' },
];
function conditionToPreset(c: BlockInstance['condition']): string {
  if (!c) return 'always';
  if (c.kind === 'viewport') return c.max != null ? 'mobile' : 'desktop';
  if (c.kind === 'until') return 'until';
  if (c.kind === 'from') return 'from';
  return 'always';
}
const TONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Domyślny' },
  { value: 'surface', label: 'Stonowany' },
  { value: 'dark', label: 'Ciemny' },
  { value: 'accent', label: 'Akcent' },
];

function BlockComposer(props: ComposerProps) {
  const { blocks, archetype, issues } = props;
  const issuesById = React.useMemo(() => {
    const m = new Map<string, CompositionIssue[]>();
    for (const i of issues) {
      if (!i.blockId) continue;
      const arr = m.get(i.blockId) ?? [];
      arr.push(i);
      m.set(i.blockId, arr);
    }
    return m;
  }, [issues]);
  const pageIssues = issues.filter((i) => !i.blockId);

  return (
    <div className="space-y-2">
      {pageIssues.length > 0 ? (
        <ul className="space-y-1">
          {pageIssues.map((i, idx) => (
            <li
              key={idx}
              className={cn(
                'flex items-start gap-1.5 rounded px-2 py-1 text-[11px]',
                i.level === 'error' ? 'bg-error/10 text-error' : 'bg-amber-500/10 text-amber-700',
              )}
            >
              <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              {i.message}
            </li>
          ))}
        </ul>
      ) : null}

      <ul className="space-y-1.5">
        {blocks.map((b, i) => (
          <BlockRow
            key={b.id}
            {...props}
            block={b}
            index={i}
            total={blocks.length}
            depth={0}
            issues={issuesById.get(b.id) ?? []}
            issuesById={issuesById}
          />
        ))}
      </ul>

      <AddBlockButton archetype={archetype} onAdd={(entry) => props.onAdd(entry)} />
    </div>
  );
}

function BlockRow({
  block,
  index,
  total,
  depth,
  archetype,
  issues,
  issuesById,
  onAdd,
  onRemove,
  onMove,
  onSetField,
  onSwitchVariant,
  onSetCondition,
  onSetTone,
  linkTargets,
}: ComposerProps & {
  block: BlockInstance;
  index: number;
  total: number;
  depth: number;
  issues: CompositionIssue[];
  issuesById: Map<string, CompositionIssue[]>;
}) {
  const handlers = { archetype, onAdd, onRemove, onMove, onSetField, onSwitchVariant, onSetCondition, onSetTone, linkTargets };
  const [open, setOpen] = React.useState(false);
  const entry = BLOCK_BY_ID[block.type];
  if (!entry) {
    return (
      <li className="rounded-md bg-error/10 px-2 py-1.5 text-xs text-error ring-1 ring-error/20">
        Nieznany blok: <code className="font-mono">{block.type}</code>
        <button type="button" onClick={() => onRemove(block.id)} className="ml-2 underline">usuń</button>
      </li>
    );
  }
  const locked = !!entry.locked;
  const variants = BLOCK_MANIFEST.filter(
    (e) => e.category === entry.category && !e.locked && archetypeAllowsEntry(e, archetype),
  );
  const hasError = issues.some((i) => i.level === 'error');

  return (
    <li
      className={cn(
        'rounded-md bg-surface ring-1 transition-colors',
        hasError ? 'ring-error/40' : 'ring-border',
      )}
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <div className="flex flex-col">
          <button
            type="button"
            disabled={locked || index === 0}
            onClick={() => onMove(block.id, -1)}
            className="text-text-subtle hover:text-text disabled:opacity-20"
            title="W górę"
          >
            <ChevronUp className="size-3" aria-hidden="true" />
          </button>
          <button
            type="button"
            disabled={locked || index === total - 1}
            onClick={() => onMove(block.id, 1)}
            className="text-text-subtle hover:text-text disabled:opacity-20"
            title="W dół"
          >
            <ChevronDown className="size-3" aria-hidden="true" />
          </button>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-xs font-medium text-text">{entry.name}</span>
            {locked ? <Lock className="size-3 text-text-subtle" aria-hidden="true" /> : null}
          </div>
          <span className="text-[10px] uppercase tracking-wide text-text-subtle">
            {CATEGORY_LABELS[entry.category] ?? entry.category}
          </span>
        </div>

        {entry.fields.length > 0 || variants.length > 1 ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={cn('rounded p-1 text-text-muted hover:bg-bg hover:text-text', open && 'bg-bg text-accent')}
            title="Edytuj treść"
          >
            <Pencil className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
        {!locked ? (
          <button
            type="button"
            onClick={() => onRemove(block.id)}
            className="rounded p-1 text-text-muted hover:bg-error/10 hover:text-error"
            title="Usuń blok"
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {issues.map((i, idx) => (
        <p
          key={idx}
          className={cn(
            'mx-2 mb-1 rounded px-1.5 py-0.5 text-[10px]',
            i.level === 'error' ? 'bg-error/10 text-error' : 'bg-amber-500/10 text-amber-700',
          )}
        >
          {i.message}
        </p>
      ))}

      {open ? (
        <div className="space-y-2.5 border-t border-border px-2.5 py-2.5">
          {variants.length > 1 ? (
            <label className="block">
              <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-subtle">Wariant</span>
              <select
                value={block.type}
                onChange={(e) => onSwitchVariant(block.id, e.target.value)}
                className="w-full rounded-md border border-border bg-bg px-2 py-1 text-xs text-text"
              >
                {variants.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          {entry.fields.map((f) => (
            <FieldControl
              key={f.key}
              field={f}
              value={(block.content ?? {})[f.key]}
              onChange={(val) => onSetField(block.id, f.key, val)}
              linkTargets={linkTargets}
              onAltText={
                f.type === 'image' && entry.fields.some((x) => x.key === 'mediaAlt')
                  ? (alt) => onSetField(block.id, 'mediaAlt', alt)
                  : undefined
              }
            />
          ))}

          {/* Visibility (typed condition) + tone knob — DUPLO-max axes that
              stay enum-only (never raw style). */}
          {!locked ? (
            <div className="grid grid-cols-2 gap-2">
              <VisibilityControl
                condition={block.condition}
                onChange={(c) => onSetCondition(block.id, c)}
              />
              <label className="block">
                <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-subtle">Ton</span>
                <select
                  value={block.layout?.tone ?? ''}
                  onChange={(e) => onSetTone(block.id, e.target.value || undefined)}
                  className="w-full rounded-md border border-border bg-bg px-2 py-1 text-xs text-text"
                >
                  {TONE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {entry.acceptsChildren ? (
            <div className="rounded-md bg-bg p-2 ring-1 ring-border">
              <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
                Bloki w środku
              </p>
              <ul className="space-y-1.5">
                {(block.children ?? []).map((child, ci) => (
                  <BlockRow
                    key={child.id}
                    {...handlers}
                    blocks={block.children ?? []}
                    issues={issuesById.get(child.id) ?? []}
                    issuesById={issuesById}
                    block={child}
                    index={ci}
                    total={(block.children ?? []).length}
                    depth={depth + 1}
                  />
                ))}
              </ul>
              {depth + 1 < MAX_BLOCK_DEPTH ? (
                <AddBlockButton
                  archetype={archetype}
                  small
                  only={entry.acceptsChildren}
                  onAdd={(e2) => onAdd(e2, block.id)}
                />
              ) : (
                <p className="mt-1.5 px-1 text-[10px] text-text-subtle">Osiągnięto maksymalne zagnieżdżenie.</p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** Visibility preset → typed Condition. Build-time (until/from) + CSS
 * (mobile/desktop). The renderer drops build-time-failing blocks and gates
 * viewport ones with a responsive class. */
function VisibilityControl({
  condition,
  onChange,
}: {
  condition: BlockInstance['condition'];
  onChange: (c: BlockInstance['condition']) => void;
}) {
  const preset = conditionToPreset(condition);
  const dateVal =
    condition && (condition.kind === 'until' || condition.kind === 'from') ? condition.date : '';
  const apply = (p: string, date?: string) => {
    switch (p) {
      case 'mobile':
        onChange({ kind: 'viewport', max: 768 });
        break;
      case 'desktop':
        onChange({ kind: 'viewport', min: 768 });
        break;
      case 'until':
        onChange({ kind: 'until', date: date ?? '' });
        break;
      case 'from':
        onChange({ kind: 'from', date: date ?? '' });
        break;
      default:
        onChange(undefined);
    }
  };
  return (
    <div>
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-subtle">Widoczność</span>
      <select
        value={preset}
        onChange={(e) => apply(e.target.value, dateVal)}
        className="w-full rounded-md border border-border bg-bg px-2 py-1 text-xs text-text"
      >
        {VISIBILITY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {preset === 'until' || preset === 'from' ? (
        <Input
          type="date"
          value={dateVal}
          onChange={(e) => apply(preset, e.target.value)}
          className="mt-1"
        />
      ) : null}
    </div>
  );
}

function FieldControl({
  field,
  value,
  onChange,
  linkTargets,
  onAltText,
}: {
  field: FieldDescriptor;
  value: unknown;
  onChange: (value: unknown) => void;
  linkTargets: LinkTarget[];
  onAltText?: (alt: string) => void;
}) {
  const str = typeof value === 'string' ? value : value == null ? '' : String(value);
  const label = (
    <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-subtle">
      {field.label}
    </span>
  );
  if (field.type === 'image') {
    return <MediaField label={label} value={str} onChange={onChange} onAltText={onAltText} />;
  }
  if (field.type === 'url') {
    return <LinkField label={label} value={str} onChange={onChange} targets={linkTargets} hint={field.hint} />;
  }
  if (field.type === 'textarea') {
    return (
      <label className="block">
        {label}
        <Textarea value={str} onChange={(e) => onChange(e.target.value)} rows={3} maxLength={field.maxLength} />
        {field.hint ? <span className="mt-0.5 block text-[10px] text-text-muted">{field.hint}</span> : null}
      </label>
    );
  }
  if (field.type === 'enum') {
    return (
      <label className="block">
        {label}
        <select
          value={str}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-border bg-bg px-2 py-1 text-xs text-text"
        >
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === 'number') {
    return (
      <label className="block">
        {label}
        <Input
          type="number"
          value={str}
          min={field.min}
          max={field.max}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      </label>
    );
  }
  // text / url / image → single-line input
  return (
    <label className="block">
      {label}
      <Input
        value={str}
        maxLength={field.maxLength}
        placeholder={field.type === 'image' ? 'URL lub identyfikator zdjęcia' : field.type === 'url' ? '/sciezka lub https://…' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {field.hint ? <span className="mt-0.5 block text-[10px] text-text-muted">{field.hint}</span> : null}
    </label>
  );
}

/** Image slot — opens the shared MediaPicker, stores the full media URL, and
 * auto-fills alt text when the asset has one. */
function MediaField({
  label,
  value,
  onChange,
  onAltText,
}: {
  label: React.ReactNode;
  value: string;
  onChange: (value: unknown) => void;
  onAltText?: (alt: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const mediaHost = useMediaHost();
  return (
    <div>
      {label}
      {value ? (
        <div className="flex items-center gap-2">
          <img src={value} alt="" className="size-12 shrink-0 rounded object-cover ring-1 ring-border" />
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>Zmień</Button>
          <Button type="button" variant="ghost" size="sm" className="text-error hover:bg-error/10" onClick={() => onChange('')}>Usuń</Button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} className="w-full justify-center">
          Wybierz zdjęcie z biblioteki
        </Button>
      )}
      {open ? (
        <MediaPicker
          title="Wybierz zdjęcie"
          selectedId={null}
          onSelect={(asset: MediaAsset) => {
            onChange(`${mediaHost}${asset.public_url}`);
            if (onAltText && asset.alt_text) onAltText(asset.alt_text);
            setOpen(false);
          }}
          onClear={() => {
            onChange('');
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** Link slot — a select of internal pages + section anchors + phone, with a
 * "własny link" escape hatch for arbitrary URLs. */
function LinkField({
  label,
  value,
  onChange,
  targets,
  hint,
}: {
  label: React.ReactNode;
  value: string;
  onChange: (value: unknown) => void;
  targets: LinkTarget[];
  hint?: string;
}) {
  const known = targets.some((t) => t.value === value);
  const [custom, setCustom] = React.useState(!known && value !== '');
  const groups = React.useMemo(() => {
    const m = new Map<string, LinkTarget[]>();
    for (const t of targets) {
      const arr = m.get(t.group) ?? [];
      arr.push(t);
      m.set(t.group, arr);
    }
    return [...m.entries()];
  }, [targets]);
  return (
    <div>
      {label}
      <select
        value={custom ? '__custom__' : value}
        onChange={(e) => {
          if (e.target.value === '__custom__') {
            setCustom(true);
          } else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
        className="w-full rounded-md border border-border bg-bg px-2 py-1 text-xs text-text"
      >
        <option value="">— brak —</option>
        {groups.map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </optgroup>
        ))}
        <option value="__custom__">Własny link…</option>
      </select>
      {custom ? (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… , /sciezka lub tel:+48…"
          className="mt-1"
        />
      ) : null}
      {hint ? <span className="mt-0.5 block text-[10px] text-text-muted">{hint}</span> : null}
    </div>
  );
}

function AddBlockButton({
  archetype,
  onAdd,
  small,
  only,
}: {
  archetype: string;
  onAdd: (entry: BlockManifestEntry) => void;
  small?: boolean;
  /** Restrict the tray to these categories (used inside containers). */
  only?: string[];
}) {
  const [open, setOpen] = React.useState(false);
  const palette = React.useMemo(() => {
    const entries = BLOCK_MANIFEST.filter(
      (e) =>
        !e.locked &&
        e.category !== 'structure' &&
        archetypeAllowsEntry(e, archetype) &&
        (!only || only.includes(e.category)),
    );
    const byCat = new Map<string, BlockManifestEntry[]>();
    for (const e of entries) {
      const arr = byCat.get(e.category) ?? [];
      arr.push(e);
      byCat.set(e.category, arr);
    }
    return [...byCat.entries()];
  }, [archetype, only]);

  return (
    <div className={small ? 'mt-1.5' : 'mt-2'}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        className="w-full justify-center"
      >
        {small ? <Plus className="size-3.5" aria-hidden="true" /> : <Layers className="size-4" aria-hidden="true" />}
        {small ? 'Dodaj do kolumn' : 'Dodaj blok'}
      </Button>
      {open ? (
        <div className="mt-1.5 max-h-72 space-y-2 overflow-y-auto rounded-md border border-border bg-bg p-2 scrollbar-thin">
          {palette.length === 0 ? (
            <p className="px-1 py-2 text-[11px] text-text-muted">Brak dostępnych bloków dla tej strony.</p>
          ) : null}
          {palette.map(([cat, entries]) => (
            <div key={cat}>
              <p className="px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
                {CATEGORY_LABELS[cat] ?? cat}
              </p>
              <ul className="space-y-0.5">
                {entries.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onAdd(e);
                        setOpen(false);
                      }}
                      className="block w-full rounded px-2 py-1 text-left hover:bg-surface"
                    >
                      <span className="block text-xs font-medium text-text">{e.name}</span>
                      <span className="block text-[10px] text-text-muted">{e.description}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DeviceToggle({ device, onChange }: { device: Device; onChange: (d: Device) => void }) {
  const items: { id: Device; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
    { id: 'desktop', icon: Monitor, label: 'Desktop' },
    { id: 'tablet', icon: Tablet, label: 'Tablet' },
    { id: 'mobile', icon: Smartphone, label: 'Mobile' },
  ];
  return (
    <div className="inline-flex items-center rounded-md bg-surface ring-1 ring-border">
      {items.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          title={label}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-md text-text-muted transition-colors',
            device === id ? 'bg-accent/10 text-accent' : 'hover:text-text'
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
